import type { CompaniesForOffshoreServicePayload } from "@services/port-services/types";
import type { AxiosResponse } from "axios";
import { api } from "./api";
import type { Berth, PortServiceSubtype, PortServiceType } from "./reference-data/types";
import type { Company, NatureIdentityDocument } from "./vessel-call/types";
import {
  type AnchorageAreas,
  type PiClub,
  type Port,
  type VesselCategory,
  type VesselClassificationSociety,
  type VesselConditions,
  type VesselType,
} from "./vessel/types";

export const REFERENCE_DATA_TYPES = {
  NATIONALITY: "nationality",
  RESTRICTED_NATIONALITIES: "restricted-nationalities",
  COUNTRIES: "countries",
  VISA_TYPES: "visa-types",
  VEHICLE_TYPES: "vehicle-types",
  SECURITY_CLEARANCE_VALIDITY_PERIODS: "security-clearance-validity-periods",
  ESERVICE_COMPANIES: "eservice-companies",
  PORTS: "ports",
  GATES: "gates",
  LOCATIONS: "locations",
  PORT_CODES: "port-codes",
  UNITS_OF_MEASUREMENT: "units-of-measurement",
  CARGOS: "cargos",
  COMPANIES: "companies",
  FROM_TO_LOCATIONS: "from-to-locations",
  VESSEL_CONDITIONS: "vessel-conditions",
  MOVEMENT_REASONS: "movement-reasons",
  RESOURCE_TYPES: "resource-types",
  RESOURCE_TYPES_ALL: "resource-types/all",
  RESOURCE: "resources",
  CREW_RANK: "crew-rank",
  NATURE_OF_CALLS: "nature-of-calls",
  COMPANIES_BY_OFFSHORE: "companies-by-offshore",
  PILOT_STATIONS: "pilot-stations",
  PORT_SERVICE_CATEGORIES: "port-service-categories",
  NATURE_IDENTITY_DOCUMENTS: "nature-identity-documents",
  VESSEL_TYPES: "vessel-types",
  CARGO_TYPES: "cargo-types",
  ANCHORAGE_AREAS: "anchorage-areas",
  BERTHS: "berths",
};

export const getPorts = async (service: string) => {
  return api.get<Port[]>(service, "ports");
};

export const getVesselTypes = async (service: string) => {
  return api.get<VesselType[]>(service, "vessel-types");
};

export const getVesselCategories = async (service: string) => {
  return api.get<VesselCategory[]>(service, "vessel-categories");
};

export const getVesselClassificationSocieties = async (service: string) => {
  return api.get<VesselClassificationSociety[]>(service, "vessel-classification-societies");
};

export const getPIClubs = async (service: string) => {
  return api.get<PiClub[]>(service, "pi-clubs");
};

export const getAnchorageAreas = async (service: string, fromToLoc?: string) => {
  const queryParams = fromToLoc ? { fromToLoc } : {};
  return api.get<AnchorageAreas[]>(service, "anchorage-areas", { params: queryParams });
};

export const getBerths = async (service: string, fromToLocation?: string | null) => {
  const queryParams = fromToLocation ? { fromToLoc: fromToLocation } : {};
  return api.get<Berth[]>(service, "berths", { params: queryParams });
};

export const getRefernceDataByType = async (type: string, service: string) => {
  return api.get(service, `${type}`);
};

export const getCompaniesForOffshoreService = async <T = Company>(
  service: string,
  payload: CompaniesForOffshoreServicePayload,
) => {
  return api.post<T[]>(service, "companies-for-offshore-service", payload);
};

export const getDocuments = async (url: string) => {
  if (!url || !/^https?:\/\//.test(url)) {
    throw new Error("Invalid URL passed to getDocuments");
  }
  const baseURL = new URL(url || "").origin;
  const path = url.substring(baseURL.length);
  const service = path.split("/")[1];
  const apiEndpoint = path.substring(service.length + 2); // +2 to account for the leading and following slash

  // Ensure you return the Blob, not the response object
  const response: AxiosResponse<Blob> = await api.get(service, apiEndpoint, {
    responseType: "blob",
    headers: {
      Accept: "application/octet-stream",
    },
  });
  return response.data ?? response; // If response.data exists, return it, else return response
};

export const getVesselConditions = async (service: string) => {
  return api.get<VesselConditions[]>(service, "vessel-conditions");
};

export const getPortServiceTypes = async (service: string, categoryCode?: string | null) => {
  const queryParam = categoryCode ? `?portServiceCategoryCode=${categoryCode}` : "";

  return api.get<PortServiceType[]>(service, `port-service-types${queryParam}`);
};

export const getPortServiceSubtypes = async (service: string, typeCode?: string | null) => {
  const queryParam = typeCode ? `?portServiceTypeCode=${typeCode}` : "";

  return api.get<PortServiceSubtype[]>(service, `port-service-subtypes${queryParam}`);
};

export const getNatureIdentityDocuments = async (service: string) => {
  return api.get<NatureIdentityDocument[]>(service, `nature-identity-documents`);
};

export const GatepassRegistrationEmirate = {
  ABU_DHABI: "ABU_DHABI",
  DUBAI: "DUBAI",
  SHARJAH: "SHARJAH",
  AJMAN: "AJMAN",
  UMM_AL_QUWAIN: "UMM_AL_QUWAIN",
  RAS_AL_KHAIMAH: "RAS_AL_KHAIMAH",
  FUJAIRAH: "FUJAIRAH",
} as const;

export type GatepassRegistrationEmirate =
  (typeof GatepassRegistrationEmirate)[keyof typeof GatepassRegistrationEmirate];

const REGISTRATION_EMIRATE_MAP: Record<string, GatepassRegistrationEmirate> = {
  "abu dhabi": GatepassRegistrationEmirate.ABU_DHABI,
  "abu_dhabi": GatepassRegistrationEmirate.ABU_DHABI,
  "abu-dhabi": GatepassRegistrationEmirate.ABU_DHABI,
  "dubai": GatepassRegistrationEmirate.DUBAI,
  "sharjah": GatepassRegistrationEmirate.SHARJAH,
  "ajman": GatepassRegistrationEmirate.AJMAN,
  "umm al quwain": GatepassRegistrationEmirate.UMM_AL_QUWAIN,
  "umm_al_quwain": GatepassRegistrationEmirate.UMM_AL_QUWAIN,
  "umm-al-quwain": GatepassRegistrationEmirate.UMM_AL_QUWAIN,
  "ras al khaimah": GatepassRegistrationEmirate.RAS_AL_KHAIMAH,
  "ras_al_khaimah": GatepassRegistrationEmirate.RAS_AL_KHAIMAH,
  "ras-al-khaimah": GatepassRegistrationEmirate.RAS_AL_KHAIMAH,
  "fujairah": GatepassRegistrationEmirate.FUJAIRAH,
};

export const toGatepassRegistrationEmirate = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;
  return REGISTRATION_EMIRATE_MAP[normalized];
};

export const getGatepassRegistrationEmirateLabel = (value?: string | null): string | undefined => {
  const enumValue = toGatepassRegistrationEmirate(value);
  if (!enumValue) return undefined;

  const enumKey = enumValue as GatepassRegistrationEmirate;
  const label = {
    [GatepassRegistrationEmirate.ABU_DHABI]: "Abu Dhabi",
    [GatepassRegistrationEmirate.DUBAI]: "Dubai",
    [GatepassRegistrationEmirate.SHARJAH]: "Sharjah",
    [GatepassRegistrationEmirate.AJMAN]: "Ajman",
    [GatepassRegistrationEmirate.UMM_AL_QUWAIN]: "Umm Al Quwain",
    [GatepassRegistrationEmirate.RAS_AL_KHAIMAH]: "Ras Al Khaimah",
    [GatepassRegistrationEmirate.FUJAIRAH]: "Fujairah",
  }[enumKey];

  return label;
};

export const DEFAULT_GATEPASS_REGISTRATION_EMIRATE = "Fujairah";

export const resolveGatepassRegistrationEmiratePrefill = (value?: string | null): string =>
  getGatepassRegistrationEmirateLabel(value) ?? DEFAULT_GATEPASS_REGISTRATION_EMIRATE;

// We have this hardcoded for the moment
export const registrationEmirates = [
  { label: "Abu Dhabi", value: "Abu Dhabi" },
  { label: "Dubai", value: "Dubai" },
  { label: "Sharjah", value: "Sharjah" },
  { label: "Ajman", value: "Ajman" },
  { label: "Umm Al Quwain", value: "Umm Al Quwain" },
  { label: "Ras Al Khaimah", value: "Ras Al Khaimah" },
  { label: "Fujairah", value: "Fujairah" },
];
